"use client";

import { useState, useEffect, useRef } from "react";
import AmbientCanvas from "./AmbientCanvas";

const testimonials = [
  {
    quote: "STYGIAN Belladonna is the most captivating fragrance I've ever worn. People stop me to ask what I'm wearing.",
    author: "Ayesha R.",
    role: "Fashion Editor, Karachi",
    accent: "#c47a8b",
  },
  {
    quote: "A masterclass in restraint and intensity. Wolfsbane doesn't announce itself — it haunts the room.",
    author: "Hamza S.",
    role: "Creative Director, Lahore",
    accent: "#5b7d9b",
  },
  {
    quote: "Finally, a house that understands darkness isn't about heaviness — it's about depth. STYGIAN gets it.",
    author: "Zainab K.",
    role: "Perfume Collector, Islamabad",
    accent: "#a8843a",
  },
];

const scores = [
  { value: "4.9", label: "of 5 — 2,300+ Connoisseurs" },
  { value: "94", label: "Sillage Rating — Haunting" },
  { value: "Gold", label: "World Perfume Awards MMXXV" },
];

function initialsOf(author: string) {
  return author
    .replace(/\./g, "")
    .split(" ")
    .map((w) => w[0])
    .join("");
}

export default function SocialProof() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const restartAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
  };

  useEffect(() => {
    restartAutoPlay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const goTo = (index: number) => {
    setActiveTestimonial((index + testimonials.length) % testimonials.length);
    restartAutoPlay();
  };

  return (
    <section
      id="voices"
      className="relative py-16 md:py-24 overflow-hidden"
      aria-label="Testimonials"
      style={{ background: "linear-gradient(180deg, #fbf8f2 0%, #f2ecdf 100%)" }}
    >
      {/* Decorative hairline */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-[#c9a84c]/40 to-transparent" />
      <div className="absolute inset-0 marble opacity-60" aria-hidden="true" />
      <div className="hairline-grid absolute inset-0 opacity-[0.14]" aria-hidden="true" />

      {/* Silk ambience */}
      <div
        className="silk left-[-7%] top-[18%] w-[32rem] h-[24rem]"
        style={{ background: "radial-gradient(ellipse at center, rgba(216,186,128,0.28), rgba(216,186,128,0.08) 55%, transparent 78%)" }}
        aria-hidden="true"
      />
      <div
        className="silk right-[-8%] bottom-[8%] w-[30rem] h-[22rem]"
        style={{ background: "radial-gradient(ellipse at center, rgba(196,122,139,0.16), rgba(196,122,139,0.05) 55%, transparent 78%)", animationDelay: "-12s" }}
        aria-hidden="true"
      />
      <div
        className="orb-glow left-[6%] bottom-[16%] w-[24rem] h-[24rem] opacity-40"
        style={{ background: "radial-gradient(circle, rgba(168,132,58,0.16) 0%, transparent 65%)" }}
        aria-hidden="true"
      />

      <AmbientCanvas variant="dust" accent="#c9a84c" density={0.4} className="opacity-40" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-3">
            What They Say
          </p>
          <h2
            className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#221d17]"
            style={{ fontWeight: 300 }}
          >
            Voices of <span className="text-shimmer-gold">Mystery</span>
          </h2>
          <div className="section-divider w-16 h-px mx-auto mt-6 bg-gradient-to-r from-transparent via-[#c9a84c]/60 to-transparent" />
        </div>

        {/* Scores / awards band */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-14">
          <div className="grid grid-cols-1 sm:grid-cols-3">
            {scores.map((score, i) => (
              <div
                key={score.label}
                className={`text-center px-4 py-5 md:py-6 ${i > 0 ? "sm:border-l sm:border-[#221d17]/10" : ""}`}
              >
                <p className="font-serif text-3xl md:text-4xl mb-2 text-[#a8843a]/85">{score.value}</p>
                <div className="w-6 h-px mx-auto mb-2 bg-[#c9a84c]/40" />
                <p className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/60 leading-relaxed">
                  {score.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial carousel */}
        <div className="max-w-3xl mx-auto">
          <div className="relative mb-8">
            {/* Prev / Next arrows */}
            <button
              onClick={() => goTo(activeTestimonial - 1)}
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-16 lg:-left-20 items-center justify-center w-11 h-11 rounded-full border border-[#c9a84c]/40 text-[#8a6a2c] bg-[#fdfbf7]/40 backdrop-blur-sm hover:border-[#c9a84c]/80 hover:text-[#6f551f] hover:bg-[#c9a84c]/10 transition-all duration-300"
              aria-label="Previous testimonial"
              data-cursor="pointer"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              onClick={() => goTo(activeTestimonial + 1)}
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-16 lg:-right-20 items-center justify-center w-11 h-11 rounded-full border border-[#c9a84c]/40 text-[#8a6a2c] bg-[#fdfbf7]/40 backdrop-blur-sm hover:border-[#c9a84c]/80 hover:text-[#6f551f] hover:bg-[#c9a84c]/10 transition-all duration-300"
              aria-label="Next testimonial"
              data-cursor="pointer"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Card */}
            <div className="gold-frame gold-glow-hover relative p-6 md:p-10 min-h-[300px] md:min-h-[260px]">
              {/* Gold corner accents */}
              <span className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#c9a84c]/50 pointer-events-none" aria-hidden="true" />
              <span className="absolute top-3 right-3 w-6 h-6 border-t border-r border-[#c9a84c]/50 pointer-events-none" aria-hidden="true" />
              <span className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#c9a84c]/50 pointer-events-none" aria-hidden="true" />
              <span className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#c9a84c]/50 pointer-events-none" aria-hidden="true" />

              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ${
                    index === activeTestimonial
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4 pointer-events-none"
                  }`}
                >
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    {/* Oversized decorative quote mark */}
                    <span
                      className="font-serif text-8xl leading-none select-none pointer-events-none -mb-8"
                      style={{ color: `${testimonial.accent}1c` }}
                      aria-hidden="true"
                    >
                      &ldquo;
                    </span>

                    <blockquote className="font-serif text-xl md:text-2xl lg:text-[26px] text-[#221d17]/85 leading-relaxed mb-4 px-2">
                      {testimonial.quote}
                    </blockquote>

                    {/* Star rating */}
                    <div className="flex items-center justify-center gap-1.5 mb-4" aria-label="Five stars">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className="text-[11px] leading-none" style={{ color: `${testimonial.accent}` }}>
                          ✦
                        </span>
                      ))}
                    </div>

                    {/* Author */}
                    <div className="flex items-center justify-center gap-4">
                      <div
                        className="w-12 h-12 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor: `${testimonial.accent}45`,
                          background: `radial-gradient(circle at 35% 30%, #ffffff, ${testimonial.accent}16)`,
                        }}
                      >
                        <span className="font-serif text-base tracking-[0.12em]" style={{ color: `${testimonial.accent}` }}>
                          {initialsOf(testimonial.author)}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-[#221d17]/85">{testimonial.author}</p>
                        <p className="text-[9px] tracking-[0.2em] uppercase text-[#8a6a2c]/70 mt-1">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Counter + Navigation */}
          <div className="flex flex-col items-center gap-3">
            <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#8a6a2c]/60">
              {String(activeTestimonial + 1).padStart(2, "0")}
              <span className="mx-2">/</span>
              {String(testimonials.length).padStart(2, "0")}
            </p>
            <div className="flex justify-center gap-3">
              {testimonials.map((testimonial, index) => (
                <button
                  key={index}
                  onClick={() => goTo(index)}
                  className={`h-[3px] rounded-full transition-all duration-500 ${
                    index === activeTestimonial ? "w-8" : "w-4 bg-[#c9a84c]/30 hover:bg-[#c9a84c]/50"
                  }`}
                  style={{
                    backgroundColor: index === activeTestimonial ? testimonial.accent : undefined,
                  }}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}